var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import moment from 'moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PlanType } from '../../../enum';
import { handleLocaleFormat } from '../../../util';
import { LINE_BUTTONS, lineBarLayout } from '../../common/chart/config';
import { scenarioHoverTemplate } from '../../common/chart/plotlyText';
import { useCurrencySymbol } from '../../hooks';
import { QueueType } from '../../multiskill/types';
import { useReportAPI } from '../../report/selectors';
import { PLAN_COLORS } from '../../wizard/constants';
import { usePeriodReportByMode } from '../selectors';
import { CHART_DECIMAL_VALUE, FINANCIAL_PERIOD_CHART_METRIC_VALUES } from './constants';
var getChartValueFormat = function (selectedMetric) { var _a; return ",.".concat((_a = CHART_DECIMAL_VALUE[selectedMetric]) !== null && _a !== void 0 ? _a : 0, "f"); };
var hoverTemplate = function (name, selectedMetric, currencySymbol) {
    if (currencySymbol === void 0) { currencySymbol = ''; }
    var format = "%{y:".concat(getChartValueFormat(selectedMetric), "}");
    return scenarioHoverTemplate(name, handleLocaleFormat(format, currencySymbol));
};
var usePeriodLabels = function (planType, startDate, periodLength) {
    var t = useTranslation('translation').t;
    var start = moment.utc(startDate);
    if (planType === PlanType.Weekly) {
        return Array.from({ length: periodLength }, function (_ignored, i) {
            var date = start.clone().add(i, 'weeks');
            var formatted = date.format(t('dateFormat.dayMonthYearFormat')).toUpperCase();
            // nosemgrep: javascript.lang.security.html-in-template-string -- a translated label, a number and a date
            return "<b>".concat(t('chart.week'), " ").concat(i + 1, "</b><br>").concat(formatted);
        });
    }
    // Monthly / MonthlyCustom
    return Array.from({ length: periodLength }, function (_ignored, i) {
        var date = start.clone().add(i, 'months');
        return date.format(t('dateFormat.monthYearFormat'));
    });
};
export var useChartMetricOptions = function (metrics) {
    var t = useTranslation('report', { keyPrefix: 'period' }).t;
    return useMemo(function () {
        return metrics.map(function (option) {
            var labelKey = "chart.metrics.".concat(option);
            return {
                id: option,
                name: t(labelKey),
            };
        });
    }, [metrics, t]);
};
export var useChartDataset = function (selectedMetric) {
    var _a, _b, _c, _d;
    var periodData = usePeriodReportByMode();
    var reportData = useReportAPI()[0].data;
    var t = useTranslation('translation').t;
    var planType = reportData === null || reportData === void 0 ? void 0 : reportData.planType;
    var startDate = reportData === null || reportData === void 0 ? void 0 : reportData.startDate;
    var periodLength = (_b = (_a = periodData === null || periodData === void 0 ? void 0 : periodData[0]) === null || _a === void 0 ? void 0 : _a.periodLength) !== null && _b !== void 0 ? _b : 0;
    var labels = usePeriodLabels(planType, startDate, periodLength);
    var chartDecimalPlaces = (_c = CHART_DECIMAL_VALUE[selectedMetric]) !== null && _c !== void 0 ? _c : 0;
    var scenarios = (_d = periodData === null || periodData === void 0 ? void 0 : periodData.map(function (scenario) {
        var _a;
        var values = (_a = scenario[selectedMetric]) !== null && _a !== void 0 ? _a : new Array(periodLength).fill(null);
        return {
            scenarioName: scenario.displayName + (scenario.dataType === QueueType.Multiskill ? " ".concat(t('multiskill')) : ''),
            colorIndex: scenario.colorIndex,
            isMultiskill: scenario.dataType === QueueType.Multiskill,
            values: values.map(function (value) { return (value == null ? value : Number(value.toFixed(chartDecimalPlaces))); }),
        };
    })) !== null && _d !== void 0 ? _d : [];
    return {
        metricKey: selectedMetric,
        labels: labels,
        scenarios: scenarios,
    };
};
export var useChartProperties = function (selectedMetric, scenarioVisibility) {
    var dataset = useChartDataset(selectedMetric);
    var reportData = useReportAPI()[0].data;
    var symbol = useCurrencySymbol(reportData === null || reportData === void 0 ? void 0 : reportData.currency).currencySymbol;
    var currencySymbol = FINANCIAL_PERIOD_CHART_METRIC_VALUES.includes(selectedMetric) ? symbol : '';
    if (!dataset)
        return null;
    var config = { modeBarButtons: LINE_BUTTONS };
    var layout = structuredClone(lineBarLayout);
    var viewRange = 7;
    var periodLength = dataset === null || dataset === void 0 ? void 0 : dataset.labels.length;
    var autoRange = periodLength <= viewRange;
    var range = [-0.5, viewRange - 0.5];
    var data = dataset.scenarios
        .filter(function (s) { return scenarioVisibility[s.scenarioName] !== false; })
        .map(function (scenario) {
        var _a;
        var color = (_a = PLAN_COLORS[scenario.colorIndex]) === null || _a === void 0 ? void 0 : _a[0];
        return {
            type: 'scatter',
            mode: 'lines+markers',
            name: scenario.scenarioName,
            x: dataset.labels,
            y: scenario.values,
            line: {
                color: color,
                width: 3,
                shape: 'spline',
                dash: scenario.isMultiskill ? 'dash' : undefined,
            },
            marker: {
                color: color,
                size: 8,
            },
            connectgaps: true,
            hovertemplate: hoverTemplate(scenario.scenarioName, selectedMetric, currencySymbol),
            hoverlabel: {
                bordercolor: '#fff',
            },
        };
    });
    if (!data.length) {
        data.push({
            type: 'scatter',
            mode: 'lines+markers',
            x: dataset.labels,
            y: dataset.labels.map(function () { return null; }),
        });
    }
    layout.yaxis = __assign(__assign({}, layout.yaxis), { showgrid: true, gridcolor: '#D7D7D7', tickformat: getChartValueFormat(selectedMetric) });
    layout.xaxis = __assign(__assign({}, layout.xaxis), { autorange: autoRange, fixedrange: !autoRange, automargin: true, showgrid: true, gridwidth: 100, range: range, rangeslider: {
            visible: !autoRange,
            thickness: 0.04,
            bgcolor: '#f1f1f1',
            range: [-0.5, periodLength - 0.5],
        } });
    return {
        data: data,
        layout: layout,
        config: config,
        xAxisConfig: {
            rangeFixed: range,
            maxRange: periodLength - 1,
            viewRange: autoRange ? periodLength : viewRange,
        },
    };
};
